import { useState } from "react";
import { Globe, Plus } from "lucide-react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DataList,

  PageHeader,
  SelectField,
  Sheet,
  StatusBadge,
  TextField,
  type DataColumn,
} from "@nahal/ui";
import type { Domain } from "@nahal/api-client";
import {
  useAddDomain,
  useDomainTargets,
  useDomains,
  useRemoveDomain,
  useWorkspaces,
  useOrganizationAccess,
} from "../../lib/queries";

/**
 * A custom domain is the one thing the customer and Nahal Labs genuinely share: the customer owns the
 * name and creates the DNS records, Nahal Labs owns the ingress and the certificate.
 *
 * So this screen is deliberately not a form that "does" anything. It is the place a customer can see
 * exactly where their domain got stuck without emailing anyone -- which is the whole reason it is
 * worth building before the verification is automated.
 */
export function DomainsScreen() {
  const domains = useDomains();
  const { canManage } = useOrganizationAccess();
  const remove = useRemoveDomain();
  const [adding, setAdding] = useState(false);
  const [showing, setShowing] = useState<Domain | null>(null);

  const columns: Array<DataColumn<Domain>> = [
    { key: "hostname", header: "Domain", primary: true, cell: (row) => row.hostname },
    {
      key: "workspace",
      header: "Workspace",
      cell: (row) => `${row.productCode} · ${row.workspaceCode}`,
    },
    {
      key: "kind",
      header: "Use",
      cell: (row) => (row.targetRef ? `Storefront · ${row.targetRef}` : "Admin"),
    },
    { key: "status", header: "Status", cell: (row) => <StatusBadge status={label(row.status)} tone={tone(row.status)} /> },
  ];

  return (
    <>
      <PageHeader
        title="Domains"
        description="Use your own domain. Add it here, create the DNS records we show you, and we take it from there."
        actions={canManage ? (
          <Button variant="solid" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
            Add domain
          </Button>
        ) : undefined}
      />

      <Card>
        <DataList
          columns={columns}
          rows={domains.data ?? []}
          rowKey={(row) => row.id}
          loading={domains.isLoading}
          error={domains.isError ? "Could not load your domains." : null}
          onRowClick={(row) => setShowing(row)}
          empty={{
            title: "No custom domain yet",
            description: "Your products work on their Nahal Labs address until you add one.",
          }}
          rowActions={(row) => (
            <>
              <Button size="sm" variant="ghost" onClick={() => setShowing(row)}>
                Setup
              </Button>
              {canManage && row.status !== "LIVE" && (
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(row.id)}>
                  Remove
                </Button>
              )}
            </>
          )}
        />
      </Card>

      <AddDomainSheet open={adding && canManage} onClose={() => setAdding(false)} />
      <SetupSheet domain={showing} onClose={() => setShowing(null)} />
    </>
  );
}

function AddDomainSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const workspaces = useWorkspaces();
  const add = useAddDomain();
  const [hostname, setHostname] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [kind, setKind] = useState("STOREFRONT");
  const [targetRef, setTargetRef] = useState("");

  // Only a provisioned workspace can serve a domain; offering the others would produce a row that
  // could never go live.
  const usable = (workspaces.data ?? []).filter((workspace) => workspace.status === "ACTIVE");
  const selected = workspaceId || usable[0]?.id || "";

  // An admin host serves the workspace; a storefront host serves one marketplace, and the product is
  // the only thing that knows which marketplaces exist.
  const targets = useDomainTargets(kind === "STOREFRONT" ? selected : "");
  const marketplaces = targets.data ?? [];
  const chosenTarget = targetRef || marketplaces[0]?.code || "";
  const needsMarketplace = kind === "STOREFRONT" && targets.isSuccess && marketplaces.length === 0;

  function reset() {
    setHostname("");
    setWorkspaceId("");
    setKind("STOREFRONT");
    setTargetRef("");
    onClose();
  }

  return (
    <Sheet
      open={open}
      title="Add a domain"
      description="Use a domain you already own. We will show you the DNS records to create."
      onClose={reset}
      footer={
        <>
          <Button variant="outline" size="lg" onClick={reset}>
            Cancel
          </Button>
          <Button
            variant="solid"
            size="lg"
            disabled={add.isPending || !hostname.trim() || !selected || needsMarketplace}
            onClick={() =>
              add.mutate(
                {
                  workspaceId: selected,
                  hostname: hostname.trim(),
                  kind,
                  targetRef: kind === "STOREFRONT" ? chosenTarget : undefined,
                },
                { onSuccess: reset },
              )
            }
          >
            {add.isPending ? "Adding..." : "Add domain"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="Domain"
          hint="Without https:// — for example shop.yourbrand.com"
          inputMode="url"
          autoCapitalize="none"
          value={hostname}
          onChange={(event) => setHostname(event.target.value)}
          required
        />
        {usable.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)]">
            You need an active workspace before a domain can point anywhere.
          </p>
        ) : (
          <SelectField
            label="Workspace"
            options={usable.map((workspace) => ({
              value: workspace.id,
              label: `${workspace.productCode} · ${workspace.name}`,
            }))}
            value={selected}
            onChange={(event) => setWorkspaceId(event.target.value)}
          />
        )}
        <SelectField
          label="Use for"
          options={[
            { value: "STOREFRONT", label: "Storefront — what your customers visit" },
            { value: "ADMIN", label: "Admin — where your team signs in" },
          ]}
          value={kind}
          onChange={(event) => {
            setKind(event.target.value);
            setTargetRef("");
          }}
        />
        {kind === "STOREFRONT" &&
          (needsMarketplace ? (
            <p className="text-sm text-[var(--text-secondary)]">
              This workspace has no marketplace yet, so there is nothing for a storefront domain to
              point at. Ask us for one from Requests first.
            </p>
          ) : (
            marketplaces.length > 0 && (
              <SelectField
                label="Marketplace"
                hint="Each marketplace needs its own domain."
                options={marketplaces.map((marketplace) => ({
                  value: marketplace.code,
                  label: marketplace.name,
                }))}
                value={chosenTarget}
                onChange={(event) => setTargetRef(event.target.value)}
              />
            )
          ))}
      </div>
    </Sheet>
  );
}

/**
 * The DNS records, and an honest account of what is happening next. The verification token is shown
 * because it is an instruction, not a secret -- the customer has to paste it into their own DNS.
 */
function SetupSheet({ domain, onClose }: { domain: Domain | null; onClose: () => void }) {
  return (
    <Sheet
      key={domain?.id ?? "none"}
      open={domain !== null}
      title={domain ? domain.hostname : "Domain setup"}
      description={domain ? explain(domain.status) : undefined}
      onClose={onClose}
      footer={
        <Button variant="outline" size="lg" onClick={onClose}>
          Close
        </Button>
      }
    >
      {domain && (
        <div className="space-y-4">
          <Card>
            <CardHeader title="Create these DNS records" />
            <CardBody className="space-y-3">
              <Record type="CNAME" name={domain.hostname} value="ingress.nahallabs.com" />
              {domain.verificationToken && (
                <Record type="TXT" name={`_nahal.${domain.hostname}`} value={domain.verificationToken} />
              )}
              <p className="text-xs text-[var(--text-secondary)]">
                DNS can take a few minutes to a few hours to spread. We check periodically — you do not
                need to tell us.
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Progress" />
            <CardBody>
              <ol className="space-y-2">
                <Step done label="Domain added" />
                <Step
                  done={domain.status === "VERIFIED" || domain.status === "LIVE"}
                  label="DNS records verified"
                />
                <Step done={domain.status === "LIVE"} label="Certificate issued and serving" />
              </ol>
              {domain.status === "FAILED" && (
                <p className="mt-3 text-sm text-[var(--text-danger,var(--text-primary))]">
                  We could not verify this domain. Check the records above, or ask us through a request.
                </p>
              )}
            </CardBody>
          </Card>

          {domain.status === "LIVE" && (
            <a
              className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-[var(--text-primary)] underline underline-offset-2"
              href={`https://${domain.hostname}`}
              target="_blank"
              rel="noreferrer"
            >
              <Globe className="size-4" />
              Open {domain.hostname}
            </a>
          )}
        </div>
      )}
    </Sheet>
  );
}

function Record({ type, name, value }: { type: string; name: string; value: string }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--border-subtle)] p-3">
      <div className="text-2xs uppercase tracking-wider text-[var(--text-muted)]">{type}</div>
      <dl className="mt-1 space-y-1">
        <div className="flex gap-2">
          <dt className="w-16 shrink-0 text-xs text-[var(--text-muted)]">Name</dt>
          <dd className="min-w-0 break-all text-sm text-[var(--text-primary)]">{name}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-16 shrink-0 text-xs text-[var(--text-muted)]">Value</dt>
          <dd className="min-w-0 break-all text-sm text-[var(--text-primary)]">{value}</dd>
        </div>
      </dl>
    </div>
  );
}

function Step({ done, label }: { done?: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <span
        className={
          done
            ? "size-2 rounded-full bg-[var(--text-primary)]"
            : "size-2 rounded-full border border-[var(--border-subtle)]"
        }
      />
      <span className={done ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]"}>{label}</span>
    </li>
  );
}

const label = (value: string) =>
  value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, " ");

const tone = (status: Domain["status"]) =>
  status === "LIVE" ? "success" : status === "FAILED" ? "danger" : "warning";

const explain = (status: Domain["status"]) =>
  status === "LIVE"
    ? "Live. Your customers can reach you on this domain."
    : status === "VERIFIED"
      ? "Records verified. We are issuing the certificate — usually a few minutes."
      : status === "FAILED"
        ? "We could not verify this domain yet."
        : "Waiting for your DNS records.";
